// src/Dashboard/pages/user/Inscription/utils/inscriptionUtils.js

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  ""
).replace(/\/+$/, "");

export function unwrapSettingsRow(response) {
  if (Array.isArray(response)) return response[0] || {};
  return response || {};
}

export function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function toAbsoluteUrl(url = "") {
  const clean = String(url || "").trim();

  if (!clean) return "";

  if (
    /^https?:\/\//i.test(clean) ||
    clean.startsWith("data:") ||
    clean.startsWith("blob:")
  ) {
    return clean;
  }

  if (!clean.startsWith("/uploads")) return clean;

  return `${API_BASE}${clean}`;
}

export function openInNewTab(url) {
  const href = toAbsoluteUrl(url);
  if (!href) return;

  window.open(href, "_blank", "noopener,noreferrer");
}

export function formatDateFR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatShortDateFR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR").format(date);
}

export function formatEUR(value) {
  const amount = Number(value || 0);

  return amount.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
}

export function toInputDate(value) {
  if (!value) return "";

  const raw = String(value);

  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
}

export function getDefaultAvatar(adherent) {
  return adherent?.genre === "Féminin"
    ? "/img/judokate_avatar.png"
    : "/img/judoka_avatar.png";
}

export function getAdherentAvatar(adherent) {
  return toAbsoluteUrl(adherent?.photo_url || adherent?.photoURL) || getDefaultAvatar(adherent);
}

export function getLicenceToSignUrl(dossier) {
  return dossier?.formulaire_licence_a_signer || "";
}

export function getLicenceSignedUrl(dossier) {
  return (
    dossier?.formulaire_licence_signe ||
    dossier?.formulaire_licence_signee ||
    ""
  );
}

export function getLicenceInsuranceUrl(dossier) {
  return (
    dossier?.licence_url ||
    dossier?.licence_assurance_url ||
    dossier?.licence_assurance ||
    ""
  );
}

export function isDriveUrl(url = "") {
  return /drive\.google\.com|docs\.google\.com|googleusercontent\.com/i.test(
    String(url || "")
  );
}

export function isImageUrl(url = "") {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(
    String(url || "").split("?")[0]
  );
}

export function isPdfUrl(url = "") {
  const clean = String(url || "").toLowerCase();

  return clean.endsWith(".pdf") || clean.includes("pdf");
}

export function getDocumentMeta(url) {
  if (!url) {
    return {
      label: "Aucun document",
      tone: "missing",
      type: "missing",
    };
  }

  if (isDriveUrl(url)) {
    return {
      label: "Lien Google Drive",
      tone: "drive",
      type: "drive",
    };
  }

  if (isPdfUrl(url)) {
    return {
      label: "PDF",
      tone: "pdf",
      type: "pdf",
    };
  }

  if (isImageUrl(url)) {
    return {
      label: "Image",
      tone: "image",
      type: "image",
    };
  }

  return {
    label: "Lien / fichier",
    tone: "link",
    type: "link",
  };
}

export function getStatusMeta(status = "") {
  switch (status) {
    case "Licencié":
      return {
        label: status,
        className:
          "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800",
      };
    case "Licencié Hors Club":
      return {
        label: status,
        className:
          "bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/40 dark:text-green-200 dark:ring-green-800",
      };
    case "Inscrit":
      return {
        label: status,
        className:
          "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800",
      };
    case "Cours d'essai":
      return {
        label: status,
        className:
          "bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-800",
      };
    case "Ancien Licencié":
      return {
        label: status,
        className:
          "bg-slate-900 text-white ring-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:ring-slate-100",
      };
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
    case "Non Licencié - Arrêt":
      return {
        label: status,
        className:
          "bg-slate-200 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
      };
    default:
      return {
        label: status || "—",
        className:
          "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      };
  }
}

export function getPaymentStatusMeta(status = "") {
  switch (status) {
    case "Réglé":
      return {
        label: status,
        className: "bg-emerald-600 text-white",
      };
    case "Partiellement Réglé":
      return {
        label: status,
        className: "bg-emerald-100 text-emerald-800",
      };
    case "En Attente":
    case "En cours":
      return {
        label: status,
        className: "bg-amber-300 text-slate-900",
      };
    case "Non Réglé":
      return {
        label: status,
        className: "bg-rose-600 text-white",
      };
    case "Remboursé":
      return {
        label: status,
        className: "bg-violet-600 text-white",
      };
    case "Annulé":
      return {
        label: status,
        className: "bg-slate-900 text-white",
      };
    default:
      return {
        label: status || "Non renseigné",
        className: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
      };
  }
}

export function computeReglementTotal(reglement) {
  if (!reglement) return 0;

  return (
    Number(reglement.montant_cotisation || 0) +
    Number(reglement.montant_lic_assurance || 0) +
    Number(reglement.reduction_foyer || 0) +
    Number(reglement.pass_sport || 0) +
    Number(reglement.reduction_saison || 0)
  );
}

export function computeDossierSummary({
  adherent,
  dossier,
  reglement,
  activeSeason,
}) {
  const isCurrentSeason =
    !!activeSeason && adherent?.saison_active === activeSeason;

  if (!dossier) {
    return {
      globalStatus: isCurrentSeason ? "no_dossier" : "renewal",
      globalLabel: isCurrentSeason ? "Dossier à créer" : "Renouvellement à faire",
      globalClassName:
        "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800",
      isCurrentSeason,
      requiredPercent: 0,
      validationPercent: 0,
      missingCount: 4,
      requiredSteps: [],
      validationSteps: [],
    };
  }

  const licenceSignedUrl = getLicenceSignedUrl(dossier);

  const requiredSteps = [
    {
      key: "cm_url",
      label: "Document médical",
      done: !!dossier.cm_url,
    },
    {
      key: "date_cm",
      label: "Date du document médical",
      done: !!dossier.date_cm,
    },
    {
      key: "licence_signed",
      label: "Formulaire licence signé",
      done: !!licenceSignedUrl,
    },
    {
      key: "reglement",
      label: "Règlement enregistré",
      done: !!reglement,
    },
  ];

  const validationSteps = [
    {
      key: "validate_cm",
      label: "Médical validé",
      done: !!dossier.validate_cm,
    },
    {
      key: "validate_licence",
      label: "Licence validée",
      done: !!dossier.validate_licence,
    },
    {
      key: "validate_reglement",
      label: "Règlement validé",
      done: !!dossier.validate_reglement,
    },
  ];

  const requiredDone = requiredSteps.filter((item) => item.done).length;
  const validationDone = validationSteps.filter((item) => item.done).length;

  const requiredPercent = Math.round((requiredDone / requiredSteps.length) * 100);
  const validationPercent = Math.round(
    (validationDone / validationSteps.length) * 100
  );

  const missingCount = requiredSteps.length - requiredDone;

  let globalStatus = "waiting";
  let globalLabel = "En attente de validation";
  let globalClassName =
    "bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:ring-blue-800";

  if (!isCurrentSeason) {
    globalStatus = "renewal";
    globalLabel = "Renouvellement à faire";
    globalClassName =
      "bg-orange-100 text-orange-800 ring-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:ring-orange-800";
  } else if (missingCount > 0) {
    globalStatus = "missing";
    globalLabel = "Documents à compléter";
    globalClassName =
      "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800";
  } else if (validationDone === validationSteps.length) {
    globalStatus = "up_to_date";
    globalLabel = "Dossier à jour";
    globalClassName =
      "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800";
  }

  return {
    globalStatus,
    globalLabel,
    globalClassName,
    isCurrentSeason,
    requiredPercent,
    validationPercent,
    missingCount,
    requiredSteps,
    validationSteps,
  };
}

export function buildAttestationState({
  adherent,
  reglement,
  settings,
  activeSeason,
}) {
  const total =
    Number(reglement?.montant_cotisation || 0) +
    Number(reglement?.montant_lic_assurance || 0);

  return {
    prenom: adherent?.prenom,
    nom: adherent?.nom,
    dateNaissance: formatShortDateFR(adherent?.date_naissance),
    adresse: adherent?.adresse,
    cp: adherent?.cp,
    commune: adherent?.commune,
    cotisation: reglement?.montant_cotisation,
    licence: reglement?.montant_lic_assurance,
    reductionFoyer: reglement?.reduction_foyer,
    passSport: reglement?.pass_sport,
    reductionSaison: reglement?.reduction_saison,
    typeReglement: reglement?.type_reglement,
    nbReglement: reglement?.nb_reglement,
    statutReglement: reglement?.statut_reglement,
    observations: reglement?.observations,
    datePaiement: new Date().toLocaleDateString("fr-FR"),
    montant: total,
    president: settings?.responsable_club,
    association: settings?.nom_club,
    logo: settings?.logo_club,
    adresse_club: settings?.adresse_club,
    tel_club: settings?.tel_club,
    mail_club: settings?.mail_club,
    siteweb_club: settings?.siteweb_club,
    siret_club: settings?.siret_club,
    saison_active: activeSeason,
  };
}

